import { deleteDocument, findDocuments, getSignedUrl, linkFile, publishFile, updateDocument, uploadFile } from '@/api/basecamp.api';
import { resolveUrlFor } from '@/router/document-relation-url-resolver';
import { MessageType, showMessage, showSnackbar } from '@/shared/messages';
import {
    DocumentCategory,
    type DocumentType,
    DocumentUploadStatus,
    FileCategory,
    type IDocument,
    type IDocumentRelation,
    type IDuplicateCheckConfig,
    type IFindDocumentsReq,
    type IMediaFile,
    type IUploadAndSaveFileResult,
    additionalDocumentTypesWhenFiltered,
    condoFileSlug,
} from '@condo/domain';
import axios from 'axios';

export interface IDocumentFilter {
    documentId?: number;
    categoryFilter: Record<DocumentCategory, DocumentType[]>;
}

export interface IFileStore {
    documents: IDocument[];
    filters: IDocumentFilter;
}

export interface IUploadData {
    fileOrFiles: File | File[];
    relationData?: Partial<IDocumentRelation>;
    docData?: Partial<IDocument>;
    mediaFileData?: Partial<IMediaFile>;
    fileCategory?: FileCategory;
    displayDuplicateSnackbar?: boolean;
    duplicateCheckConfig?: IDuplicateCheckConfig;
    folderId?: number;
}

const getInitialState = () => {
    return {
        documents: [],
        filters: {
            categoryFilter: Object.values(DocumentCategory).reduce((acc, category) => {
                acc[category] = [];
                return acc;
            }, {}) as Record<DocumentCategory, DocumentType[]>,
            documentId: undefined,
        },
    };
};

const state = getInitialState();

const getters = {
    all(state: IFileStore): IDocument[] {
        return state.documents.filter((d: IDocument) => {
            const isCategoryFilterApplied = Object.values(state.filters.categoryFilter).some(filter => filter.length);
            const isMatchingFilters = state.filters.categoryFilter[d.documentCategory]?.length
                ? state.filters.categoryFilter[d.documentCategory].some(
                      filteredType =>
                          filteredType === d.documentType || additionalDocumentTypesWhenFiltered[filteredType as DocumentType]?.includes(d.documentType),
                  )
                : !isCategoryFilterApplied;
            return isMatchingFilters && (!state.filters.documentId || state.filters.documentId === d.documentId);
        });
    },
    filters(state: IFileStore) {
        return state.filters;
    },
};

const baseDocumentUpload = async (
    { commit, dispatch },
    { fileOrFiles, relationData, docData, mediaFileData, duplicateCheckConfig, fileCategory = FileCategory.Document, folderId }: IUploadData,
): Promise<{ status: DocumentUploadStatus; file: IDocument; uploadResponse?: string }[]> => {
    let bulkUploadSize = 0;
    const fileData = {
        ...relationData,
        ...docData,
        ...mediaFileData,
        folderId,
    };
    const sizeLimit = 30000000; // bytes, 28.61 MB in fact,
    // the G cloud run is about 30MB but better to be on the safe side

    const formData = new FormData();
    const mixedUploads = [];
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];

    files.forEach((file, index) => {
        if (bulkUploadSize + file.size < sizeLimit) {
            formData.append(`file_${index}_generic`, file);
            bulkUploadSize += file.size;
        } else {
            showMessage("Hold tight! We're in the middle of the upload, please do not leave the page.");
            // to big or out of total request size, see sizeLimit (this is limitation from G cloud run)
            mixedUploads.push(dispatch('uploadFileWithSignedUrl', { file, fileData, fileCategory }));
        }
    });

    // some bytes to upload in a bulk
    if (bulkUploadSize > 0) {
        mixedUploads.push(uploadFile(formData, { ...fileData, fileCategory, duplicateCheckConfig }));
    }

    return Promise.all(mixedUploads).then(res =>
        res.flatMap(files => {
            const newFiles = files.filter(({ status }) => status === DocumentUploadStatus.New);
            newFiles.forEach(({ file }) => commit('addDocument', { ...file, isNew: true }));

            return files;
        }),
    );
};

const actions = {
    async getDocument({ commit }, params: IFindDocumentsReq) {
        return findDocuments(params).then((documents: IDocument[]) => {
            commit('documents', documents);
            return documents;
        });
    },

    async uploadDocuments(ctx, data: IUploadData): Promise<IUploadAndSaveFileResult[]> {
        const uploadResults = await baseDocumentUpload(ctx, data);
        const duplicateResults = uploadResults.filter(result => result.status === DocumentUploadStatus.Duplicate);
        const uploadResponses = uploadResults.filter(result => !!result.uploadResponse).map(r => r.uploadResponse!);
        if (uploadResponses.length) {
            uploadResponses.forEach(response => showSnackbar(response, MessageType.SuccessDark));
        }

        if (duplicateResults?.length && (typeof data.displayDuplicateSnackbar === 'undefined' || data.displayDuplicateSnackbar)) {
            const urlsForResults = duplicateResults
                .map(result => {
                    const url = resolveUrlFor(result.file);
                    if (url) {
                        return `· <a class="color-white-bold" href="${url}" target="_blank">${result.file.filename}</a>`;
                    }
                    return `<b>${result.file.filename}</b>`;
                })
                .join('<br />');
            showSnackbar(`Some uploaded files are duplicated: <br /><br />${urlsForResults}<br />`, MessageType.Danger);
        }
        return uploadResults;
    },
    async uploadFileWithSignedUrl(store, { file, fileData, fileCategory }) {
        return getSignedUrl({ uuid: condoFileSlug(file.name, fileData) }).then(response => {
            const { bucketFilename, url } = response;
            return axios
                .create({
                    headers: {
                        'Content-Type': file.type || 'application/octet-stream',
                        'Access-Control-Allow-Origin': '*',
                    },
                })
                .put(url, file)
                .then(() => {
                    // Has defined relations
                    if (Object.values(fileData).some(val => val)) {
                        return linkFile({
                            ...fileData,
                            bucketFilename,
                            originalFileName: file.name,
                            source: 'generic',
                            fileCategory,
                        });
                    }
                    return publishFile(bucketFilename);
                });
        });
    },
    async deleteDocument({ commit }, { uuid }) {
        return deleteDocument(uuid).then(() => commit('deleteDocument', uuid));
    },
    async updateDocument({ commit }, documentDetails: Partial<IDocument>) {
        return updateDocument(documentDetails).then(document => commit('replaceDocument', document));
    },
};

const mutations = {
    documents(state: IFileStore, documents: IDocument[]) {
        state.documents = documents;
    },
    addDocument(state: IFileStore, document: IDocument) {
        state.documents.push(document);
    },
    replaceDocument(state: IFileStore, document: IDocument) {
        const index = state.documents.findIndex(d => d.documentId === document.documentId);
        state.documents[index] = document;
    },
    deleteDocument(state: IFileStore, uuid: string) {
        state.documents = state.documents.filter(document => document.uuid !== uuid);
    },
    addDocumentIdFilter(state: IFileStore, documentId: number) {
        if (documentId) {
            state.filters = {
                ...state.filters,
                documentId,
            };
        }
    },
    toggleFilters(state: IFileStore, filter: { category: DocumentCategory; type: DocumentType; documentId?: Number }) {
        if (state.filters.categoryFilter[filter.category].includes(filter.type)) {
            state.filters.categoryFilter[filter.category] = state.filters.categoryFilter[filter.category].filter(item => item !== filter.type);
        } else {
            state.filters.categoryFilter[filter.category].push(filter.type);
        }
    },
    resetFilters(state: IFileStore) {
        state.filters = getInitialState().filters;
    },
};

export const documentsStore = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
